import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  getJob,
  toggleSavedJob,
  checkJobSaved,
  applyJob,
  checkJobApplied,
} from "../../api/jobs";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { getFullImageUrl } from "../../utils/imageHandler";
import Loader from "../../components/ui/Loader";
import SEOHead from "../../components/SEO/SEOHead";
import { getJobPath, isJobExpired } from "../../utils/jobSlug";
import {
  formatJobSalary,
  formatJobExperience,
  formatJobType,
  formatJobText,
} from "../../utils/jobFormat";
import { glassCard, glassStat, glassPanel } from "../../utils/glassStyles";
import {
  MapPin,
  Calendar,
  Briefcase,
  DollarSign,
  Users,
  Star,
  ArrowLeft,
  Share2,
  Bookmark,
  Zap,
  Loader2,
  Clock,
  Building2,
  ExternalLink,
  Globe,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/client";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";

const ShareModal = React.lazy(() => import("../../components/common/ShareModal"));

function JobTextBlock({ title, icon: Icon, text, bulletClass = "bg-primary" }) {
  const lines = formatJobText(text);
  if (!lines.length) return null;

  return (
    <div className={glassPanel}>
      <h3 className="text-lg sm:text-xl font-bold mb-5 flex items-center gap-2.5">
        {Icon && <Icon className="w-5 h-5 text-orange-500" />}
        {title}
      </h3>
      <div className="space-y-3 text-[15px] leading-7 text-foreground/85 dark:text-foreground/80">
        {lines.map((line, i) => {
          const isBullet = /^[-•*]\s/.test(line) || /^\d+[.)]\s/.test(line);
          const content = line.replace(/^[-•*]\s/, "").replace(/^\d+[.)]\s/, "");

          if (isBullet) {
            return (
              <div key={i} className="flex gap-3 items-start">
                <span className={`mt-2.5 h-1.5 w-1.5 rounded-full shrink-0 ${bulletClass}`} />
                <p className="flex-1">{content}</p>
              </div>
            );
          }

          return (
            <p key={i} className="whitespace-pre-wrap">
              {line}
            </p>
          );
        })}
      </div>
    </div>
  );
}

export default function JobDetails() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [showApplicationDialog, setShowApplicationDialog] = useState(false);
  const [applying, setApplying] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [profileComplete, setProfileComplete] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);

  useEffect(() => {
    fetchJobDetails();
    if (user?.role === "APPLIER") {
      checkIfSaved();
      checkProfileStatus();
      checkIfApplied();
    } else {
      setIsSaved(false);
      setHasApplied(false);
      setProfileComplete(false);
    }
  }, [jobId, user]);

  const fetchJobDetails = async () => {
    if (!jobId || jobId === "undefined") {
      toast.error("Invalid Job URL");
      navigate("/");
      return;
    }
    try {
      const data = await getJob(jobId);
      setJob(data.data);
    } catch (error) {
      console.error("fetchJobDetails error:", error);
      if (error.response?.status === 404) {
        toast.error("Job not found");
      } else {
        toast.error("Failed to load job details");
      }
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const checkIfSaved = async () => {
    try {
      const status = await checkJobSaved(jobId);
      setIsSaved(status);
    } catch (error) {
      console.error("Error checking saved status", error);
    }
  };

  const checkIfApplied = async () => {
    try {
      const applied = await checkJobApplied(jobId);
      setHasApplied(applied);
    } catch (error) {
      console.error("Error checking applied status", error);
    }
  };

  const checkProfileStatus = async () => {
    try {
      const res = await api.get("/applier/profile");
      const profile = res.data;
      const isComplete =
        !!profile &&
        !!profile.fullName &&
        !!profile.education &&
        !!profile.phone &&
        !!profile.university &&
        profile.gpa != null &&
        !!profile.gender &&
        !!profile.resumeUrl;
      setProfileComplete(isComplete);
    } catch (error) {
      console.error("Error checking profile status", error);
      setProfileComplete(false);
    }
  };

  const handleToggleSave = async () => {
    if (!user) return toast.error("Please login to save jobs");
    try {
      const res = await toggleSavedJob(jobId);
      setIsSaved(res.isSaved);
      toast.success(res.isSaved ? "Job saved!" : "Job removed from saved");
    } catch (error) {
      toast.error("Action failed");
    }
  };

  const handleApplyClick = () => {
    if (!user) return toast.error("Please login to apply");
    if (isJobExpired(job)) return toast.error("This job has expired and is no longer accepting applications.");
    if (hasApplied) return toast.error("You have already applied for this job.");
    setShowApplicationDialog(true);
  };

  const handleQuickApply = async () => {
    setApplying(true);
    try {
      await applyJob(jobId);
      toast.success("Application submitted successfully!");
      setHasApplied(true);
      setShowApplicationDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Application failed");
    } finally {
      setApplying(false);
    }
  };

  const handleGoToProfile = () => {
    setShowApplicationDialog(false);
    navigate("/applier/profile-form");
  };

  if (loading) return <Loader />;
  if (!job) return null;

  const expired = isJobExpired(job);
  const companyName = job.companyName || job.recruiter?.companyName || "Company";
  const companyWebsite = job.companyWebsite || job.recruiter?.website;
  const websiteHref = companyWebsite
    ? (companyWebsite.startsWith("http") ? companyWebsite : `https://${companyWebsite}`)
    : null;
  const websiteLabel = companyWebsite?.replace(/^https?:\/\//i, "").replace(/\/$/, "");
  const jobUrl = getJobPath(job);
  const seoDescription = `${job.title} at ${companyName}${job.location ? ` in ${job.location}` : ""}. ${(job.description || "").slice(0, 150)}...`;

  return (
    <div className="min-h-screen bg-transparent relative">
      <SEOHead
        title={`${job.title} at ${companyName} | Shebeka Jobs`}
        description={seoDescription}
        keywords={`${job.title}, ${companyName}, jobs Ethiopia, ${job.location || ""}, ${job.category || ""}`}
        url={jobUrl}
        type="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "JobPosting",
          title: job.title,
          description: job.description,
          datePosted: job.createdAt,
          validThrough: job.deadline,
          employmentType: job.type,
          hiringOrganization: { "@type": "Organization", name: companyName },
          jobLocation: { "@type": "Place", address: job.location },
        }}
      />

      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-5 sm:py-8 md:py-12 pb-20 md:pb-12">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-8 group text-foreground/70 hover:text-foreground hover:bg-white/20 dark:hover:bg-white/10 backdrop-blur-sm"
        >
          <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          Back
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 lg:items-start">
          <div className="lg:col-span-8 space-y-6">
            <Card className={`${glassCard} rounded-[2rem] overflow-hidden border-0`}>
              <div className="p-6 sm:p-10 space-y-8">
                <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-6">
                  <div className="flex flex-col items-center sm:items-start gap-4">
                    <Avatar className="w-20 h-20 rounded-2xl border-2 border-white/40 shadow-xl overflow-hidden bg-white/80 backdrop-blur-sm">
                      {(job.companyLogo || job.recruiter?.logoUrl) && (
                        <img
                          src={getFullImageUrl(job.companyLogo || job.recruiter?.logoUrl)}
                          alt="Company Logo"
                          className="w-full h-full object-contain p-1"
                        />
                      )}
                      <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-black text-2xl uppercase">
                        {companyName[0] || "J"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-center sm:text-left space-y-2">
                      <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                        <h1 className="font-display text-xl sm:text-2xl md:text-3xl font-bold tracking-tight leading-tight">{job.title}</h1>
                        {expired && (
                          <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-400/30 backdrop-blur-sm px-3 py-1 rounded-xl font-bold uppercase tracking-widest text-[10px]">
                            Expired
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 text-sm">
                        <span className="text-primary font-bold">{companyName}</span>
                        {websiteHref && (
                          <>
                            <span className="w-1 h-1 rounded-full bg-muted-foreground/50 hidden sm:block" />
                            <a
                              href={websiteHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-muted-foreground font-medium flex items-center gap-1 hover:text-primary transition-colors"
                            >
                              <Globe className="w-3.5 h-3.5" />
                              {websiteLabel}
                            </a>
                          </>
                        )}
                        <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
                        <span className="text-muted-foreground font-medium flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" /> {job.location || "Remote"}
                        </span>
                      </div>
                      {(job.recruiterId || job.recruiter?.id) && (
                        <Link
                          to={`/companies/${job.recruiterId || job.recruiter.id}`}
                          className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-primary hover:underline"
                        >
                          <Building2 className="w-4 h-4" />
                          View company profile
                          <ExternalLink className="w-3 h-3 opacity-70" />
                        </Link>
                      )}
                    </div>
                  </div>
                  <Badge className="bg-primary/15 text-primary border-primary/25 backdrop-blur-sm px-4 py-1.5 rounded-xl font-bold uppercase tracking-widest text-[10px]">
                    {formatJobType(job)}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                  <div className={glassStat}>
                    <DollarSign className="w-5 h-5 mx-auto mb-2 text-primary dark:text-accent" />
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Salary</p>
                    <p className="text-xs sm:text-sm font-bold mt-1 leading-snug">{formatJobSalary(job)}</p>
                  </div>
                  <div className={glassStat}>
                    <Briefcase className="w-5 h-5 mx-auto mb-2 text-primary" />
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Experience</p>
                    <p className="text-xs sm:text-sm font-bold mt-1">{formatJobExperience(job)}</p>
                  </div>
                  <div className={glassStat}>
                    <Users className="w-5 h-5 mx-auto mb-2 text-primary" />
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Category</p>
                    <p className="text-xs sm:text-sm font-bold mt-1 truncate">{job.category || "General"}</p>
                  </div>
                  <div className={glassStat}>
                    <Calendar className="w-5 h-5 mx-auto mb-2 text-accent" />
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Posted</p>
                    <p className="text-xs sm:text-sm font-bold mt-1">{format(new Date(job.createdAt), "MMM d, yyyy")}</p>
                  </div>
                </div>

                {job.deadline && (
                  <div className={`${glassStat} flex items-center justify-center gap-2 py-3`}>
                    <Clock className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium">
                      Deadline: {format(new Date(job.deadline), "MMM d, yyyy")}
                    </span>
                  </div>
                )}
              </div>
            </Card>

            <JobTextBlock title="Role Overview" text={job.description} bulletClass="bg-primary" />
            {job.requirements && (
              <JobTextBlock title="Requirements" icon={Star} text={job.requirements} bulletClass="bg-accent" />
            )}
          </div>

          <div className="lg:col-span-4 space-y-5 lg:sticky lg:top-24">
            <Card className={`${glassCard} rounded-3xl overflow-hidden border-0 bg-gradient-to-br from-primary/10 via-accent/10 to-accent/10 dark:from-primary/15 dark:via-accent/10 dark:to-accent/15`}>
              <CardContent className="p-7 sm:p-8">
                <h3 className="text-2xl font-black mb-3 leading-tight">Ready for the next step?</h3>
                <p className="text-muted-foreground font-medium mb-6 text-sm leading-relaxed">
                  {expired
                    ? "This posting has expired and is no longer accepting applications."
                    : `Apply now to showcase your skills to ${companyName}.`}
                </p>
                <div className="space-y-3">
                  <Button
                    onClick={handleApplyClick}
                    disabled={expired || ["ADMIN", "RECRUITER"].includes(user?.role) || hasApplied}
                    className="w-full h-12 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-base shadow-lg shadow-primary/25 disabled:opacity-50"
                  >
                    {expired
                      ? "Applications Closed"
                      : ["ADMIN", "RECRUITER"].includes(user?.role)
                        ? "Appliers Only"
                        : hasApplied
                          ? "Already Applied"
                          : user
                            ? "Apply Now"
                            : "Login to Apply"}
                  </Button>
                  <Button
                    onClick={handleToggleSave}
                    variant="outline"
                    className={`w-full h-12 rounded-2xl font-bold backdrop-blur-sm border-white/30 ${
                      isSaved ? "bg-primary/20 text-primary" : "bg-white/20 dark:bg-white/5"
                    }`}
                  >
                    {isSaved ? (
                      <Star className="w-5 h-5 mr-2 fill-current text-yellow-500" />
                    ) : (
                      <Bookmark className="w-5 h-5 mr-2" />
                    )}
                    {isSaved ? "Saved" : "Save Job"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className={`${glassCard} rounded-3xl border-0`}>
              <CardContent className="p-6">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground/70 text-center mb-4">
                  Share with friends
                </p>
                <Button
                  onClick={() => setShareDialogOpen(true)}
                  variant="secondary"
                  className="w-full h-12 rounded-xl font-bold bg-white/30 dark:bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/50 dark:hover:bg-white/15"
                >
                  <Share2 className="w-4 h-4 mr-2" /> Share this opportunity
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <ShareModal
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        title={`${job.title} at ${companyName}`}
        url={`${window.location.origin}${jobUrl}`}
        description="Help a friend find their next dream job."
      />

      <Dialog open={showApplicationDialog} onOpenChange={setShowApplicationDialog}>
        <DialogContent className="rounded-[2rem] bg-white/70 dark:bg-gray-900/80 backdrop-blur-3xl border border-white/30 dark:border-white/10 max-w-md w-[95vw] p-0 overflow-hidden">
          <DialogHeader className="p-8 text-center bg-white/30 dark:bg-white/5">
            <div className="w-16 h-16 bg-primary/15 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
              <Zap className="w-8 h-8 text-accent" />
            </div>
            <DialogTitle className="text-2xl font-black">How would you like to apply?</DialogTitle>
            <DialogDescription className="font-medium">
              You're applying for <span className="text-foreground font-semibold">{job.title}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="p-8 space-y-5">
            <div className="rounded-2xl border border-white/25 dark:border-white/10 bg-white/30 dark:bg-white/5 backdrop-blur-xl p-6 space-y-4">
              <p className="text-sm font-medium text-muted-foreground">
                Use your current profile to send a quick application.
              </p>
              {!profileComplete && (
                <p className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                  We noticed your profile may be incomplete. You can still apply now or update your profile first.
                </p>
              )}
              <Button
                onClick={handleQuickApply}
                disabled={applying || expired}
                className="w-full h-12 rounded-xl font-bold bg-primary hover:bg-primary/90 text-white"
              >
                {applying ? <Loader2 className="w-5 h-5 animate-spin" /> : "Apply now with my profile"}
              </Button>
            </div>
            <div className="rounded-2xl border border-dashed border-white/30 dark:border-white/15 bg-white/15 dark:bg-white/[0.03] p-6">
              <Button onClick={handleGoToProfile} variant="outline" className="w-full h-12 rounded-xl font-bold backdrop-blur-sm">
                Update my profile & apply later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
